import { useEffect, useState } from "react";
import AppleSignin from "react-apple-signin-auth";
import { api } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_SERVICES_ID;
const APPLE_REDIRECT_URI = process.env.REACT_APP_APPLE_REDIRECT_URI;
const APPLE_ENABLED = Boolean(APPLE_CLIENT_ID && APPLE_REDIRECT_URI);

const SESSION_NONCE = (() => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `apple-link-${Date.now()}`;
})();

const fetchLinkedAccounts = async () => {
  const { data } = await api.get("/auth/linked-accounts");
  return data;
};

const ProviderRow = ({ label, linked, action, testid }) => (
  <div
    className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800/40 rounded border dark:border-gray-700"
    data-testid={testid}
  >
    <div className="flex items-center gap-2 text-sm">
      {linked ? (
        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
      ) : (
        <span className="text-gray-400 dark:text-gray-500 text-xs px-1">
          —
        </span>
      )}
      <span className="text-gray-900 dark:text-gray-100">{label}</span>
      {linked && (
        <span className="text-xs text-emerald-700 dark:text-emerald-400">
          Linked
        </span>
      )}
    </div>
    {action}
  </div>
);

/**
 * Linked Accounts card — Settings dialog section.
 *
 * Shows which sign-in providers are linked to the currently logged-in
 * user and lets them link Apple Sign-In on demand. Eliminates the need
 * for the admin merge curl in the common case: a user who already has
 * Google + then enrolls in Apple Sign-In can now link the two from
 * inside the app instead of contacting an admin.
 *
 * Backend contract:
 *   GET  /api/auth/linked-accounts   — read current link state
 *   POST /api/auth/apple/link        — attach apple_sub to current user
 *                                       (absorbs any orphan with same sub)
 *
 * Force-flow: when the backend reports the Apple account is already
 * linked to a DIFFERENT user that owns data, we surface that and offer
 * a `force=true` retry. We DON'T expose force as a default checkbox to
 * avoid accidental orphan deletion when it isn't truly an orphan.
 */
export const LinkedAccountsCard = () => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);
  const [forcePrompt, setForcePrompt] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchLinkedAccounts()
      .then((data) => {
        if (!cancelled) setState(data);
      })
      .catch(() => {
        /* surfaced via the "—" state below */
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const reload = () => {
    fetchLinkedAccounts()
      .then(setState)
      .catch(() => {});
  };

  const postLink = async (idToken, displayName, { force = false } = {}) => {
    setLinking(true);
    try {
      const { data } = await api.post(
        `/auth/apple/link${force ? "?force=true" : ""}`,
        { id_token: idToken, user_name: displayName || undefined },
      );
      if (data.absorbed_orphan_email) {
        toast.success(
          `Apple linked. Merged orphan account: ${data.absorbed_orphan_email}`,
        );
      } else {
        toast.success("Apple Sign-In is now linked to this account.");
      }
      setForcePrompt(null);
      reload();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (
        status === 409 &&
        typeof detail === "string" &&
        detail.includes("owns")
      ) {
        setForcePrompt({ idToken, displayName, message: detail });
      } else {
        toast.error(detail || "Failed to link Apple account.");
      }
    } finally {
      setLinking(false);
    }
  };

  const handleAppleSuccess = (response) => {
    const idToken = response?.authorization?.id_token;
    if (!idToken) {
      toast.error("Apple sign-in returned no token — please try again.");
      return;
    }
    const fullName = response?.user?.name;
    const displayName = fullName
      ? [fullName.firstName, fullName.lastName]
          .filter(Boolean)
          .join(" ")
          .trim()
      : undefined;
    postLink(idToken, displayName);
  };

  const handleAppleError = (err) => {
    if (err?.error === "popup_closed_by_user") return;
    toast.error("Apple sign-in was interrupted. Please try again.");
  };

  if (loading) {
    return (
      <div className="space-y-2">
        <Label>Linked Accounts</Label>
        <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading…
        </div>
      </div>
    );
  }

  if (!state) return null;

  return (
    <div className="space-y-2" data-testid="linked-accounts-card">
      <Label>Linked Accounts</Label>
      <p className="text-xs text-gray-500 mb-2">
        Sign in with any of your linked providers and land on the same
        account. The email Apple verifies for you is automatically added
        as an alternate.
      </p>

      <ProviderRow
        label="Google"
        linked={state.google_linked}
        testid="linked-google"
      />
      <ProviderRow
        label="Apple"
        linked={state.apple_linked}
        testid="linked-apple"
        action={
          state.apple_linked || !APPLE_ENABLED ? null : (
            <AppleSignin
              authOptions={{
                clientId: APPLE_CLIENT_ID,
                scope: "email name",
                redirectURI: APPLE_REDIRECT_URI,
                state: "origin:link",
                nonce: SESSION_NONCE,
                usePopup: true,
              }}
              onSuccess={handleAppleSuccess}
              onError={handleAppleError}
              render={(renderProps) => (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={renderProps.onClick}
                  disabled={linking}
                  data-testid="link-apple-button"
                >
                  {linking ? (
                    <>
                      <Loader2 className="h-3 w-3 animate-spin mr-1" />
                      Linking…
                    </>
                  ) : (
                    "Link Apple"
                  )}
                </Button>
              )}
            />
          )
        }
      />
      <ProviderRow
        label="Email + password"
        linked={state.email_password_set}
        testid="linked-email"
      />

      {forcePrompt && (
        <div
          className="mt-3 p-3 rounded border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800"
          data-testid="force-link-prompt"
        >
          <div className="flex items-start gap-2 mb-2">
            <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-900 dark:text-amber-200">
              {forcePrompt.message}
            </p>
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
            Forcing will permanently delete the orphan user record. Data
            owned by the orphan (trips, segments) will NOT be migrated.
            Cancel if you&apos;re not sure.
          </p>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="destructive"
              onClick={() =>
                postLink(forcePrompt.idToken, forcePrompt.displayName, {
                  force: true,
                })
              }
              disabled={linking}
              data-testid="force-link-confirm-btn"
            >
              Force link
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setForcePrompt(null)}
              disabled={linking}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LinkedAccountsCard;
